import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'

import { AnimatePresence } from 'framer-motion'
import { Button, Tag, PopOut, Animation, Schedule } from 'components'
import { ContextInput, PrioritySelector, PageSelector, DateSelector, TagSelector, ToggleButton } from 'features'
import { FaFlag, FaTags, FaCalendarDay, FaFileAlt, FaAngleRight, FaAngleUp, FaExclamation } from 'react-icons/fa'

import { itemContext } from 'context'
import * as actionCreators from 'store/actions'
import { scheduleString } from 'utils'

const tagIconStyle = {
    margin: '0 0.4rem 0 0',
    fontSize: '0.8rem',
    color: 'var(--secondary-accent-color)'
    // color: 'var(--text-color)'
}

const tagStyle = {
    backgroundColor: 'var(--primary-accent-color)',
    padding: '0.2rem 0.5rem',
    margin: '0 0.5rem 0.3rem 0'
}

const Container = styled.div`
    background-color: var(--secondary-color);
    width: 17rem;
    font-size: var(--popout-font-size);
    padding: 0.5rem;
    border-radius: 5px;
    overflow: hidden;
    position: relative;
    margin-top: 0.5rem;
    box-shadow: var(--box-shadow);
    :focus{
        outline: none;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const Title = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0 0 0.25rem 0.5rem;
    text-transform: uppercase;
    color: var(--secondary-accent-color);
`

const Subtitle = styled.div`
    font-weight: bolder;
    font-size: 0.7rem;
    padding: 0 0 0.25rem 0.5rem;
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
`

const Row = styled.div`
    display: grid;
    grid-template-columns: 1rem 3rem 1fr 1rem;
    grid-column-gap: 0.5rem;
    align-items: center;
    padding: 0.5rem 0.5rem;
    background-color: var(--secondary-color);
    border-radius: 5px;
    cursor: pointer;
    :hover{
        filter: brightness(var(--brightness));
    }
`

const Text = styled.div`
    font-weight: bold;
    font-size: 0.8rem;
`

const Icon = styled.div`
    color: ${props => props.color || 'var(--text-color)'};
    display: flex;
    font-size: 0.8rem;
`

const SelectedWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1rem;
    grid-column-gap: 0.5rem;
    border-bottom: 1px solid var(--primary-accent-color);
`

const SelectedContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow: hidden;
    padding: ${props => props.show && '0 0 0.5rem 0'};
    border-bottom: ${props => props.selected && '1px solid var(--primary-accent-color)'};
    margin: ${props => props.show ? '0.5rem 0 0 0' : props.selected ? '0 0 0.5rem 0' : null};
`

const FilterOptions = props => {
    const {
        pageID,
        closeHandler
    } = props;
    const dispatch = useDispatch()
    const uid = useSelector(state => state.auth.uid)
    const user = useSelector(state => state.auth.user)
    const sessionId = useSelector(state => state.content.sessionId)
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const page = data['pages'][pageID]
    const filtered = page['filters'] && page['filters'] && Object.values(page['filters']).some(value => value)
    // console.log(page['filters'], filtered)

    // Filter state
    const [tab, setTab] = React.useState(null)

    const onSelect = (value, type) => {
        let updatedFilters = {...page['filters']} || {}
        if(['PRIORITY', 'TAGS', 'PAGES'].includes(type)){
            let updatedValue = updatedFilters[type] || []
            if(updatedValue.includes(value)){
                updatedValue.splice(updatedValue.indexOf(value),1)
            } else {
                updatedValue.push(value)
            }
            
            if(updatedValue.length > 0){
                updatedFilters[type] = updatedValue
            } else {
                delete updatedFilters[type]
            }
            
        } else if (type === 'DATE') {
            const start_date = value[0]
            const end_date = !value[1] ? value[0] : value[1]
            updatedFilters['START_DATE'] = start_date
            updatedFilters['END_DATE'] = end_date
        }

        let updatedPage = {...page}
        updatedPage['filters'] = updatedFilters
        dispatch(actionCreators.updatePage(updatedPage, data, uid, sessionId))
    }

    const onClear = () => {
        let updatedPage = {...page}
        updatedPage['filters'] = null
        dispatch(actionCreators.updatePage(updatedPage, data, uid, sessionId))
        closeHandler && closeHandler()
    }

    let blocks = []
    let selected = []
    const schedule = page['filters'] && page['filters']['START_DATE'] && scheduleString({ start_date: page['filters']['START_DATE'], end_date: page['filters']['END_DATE'] }, user['timezone'])
    const options = {
        PRIORITY: {
            title: 'Priority',
            icon: <FaFlag/>,
            value: page['filters'] && page['filters']['PRIORITY'] && page['filters']['PRIORITY'].map(priority => 
                <Tag label={itemContext.PRIORITY_TITLE[priority]} 
                    icon={<FaFlag style={{...tagIconStyle}}/>}
                    styles={tagStyle}
                    // styles={{...tagStyle, backgroundColor: itemContext.PRIORITY_COLOR[priority]}}
                    onDelete={() => onSelect(priority, 'PRIORITY')}
                    />
            ),
            input: <PrioritySelector onSelect={value => onSelect(value,'PRIORITY')}
                        selected={page['filters'] && page['filters']['PRIORITY']}
                        styles={{ boxShadow: 'transparent', border: '1px solid transparent' }}/>
        },
        TAGS: {
            title: 'Tags',
            icon: <FaTags/>,
            value: page['filters'] && page['filters']['TAGS'] && page['filters']['TAGS'].map(tagID => 
                <Tag label={data['tags'][tagID]['content']} 
                    tooltip={!pageID && data['pages'][data['tags'][tagID]['pageID']]['page_name']}
                    icon={<FaTags style={{...tagIconStyle}}/>}
                    styles={tagStyle}
                    // styles={{...tagStyle, backgroundColor: data['tags'][tagID]['color']}}
                    onDelete={() => onSelect(tagID, 'TAGS')}
                    />
            ),
            input: <TagSelector onSelect={(tagID) => onSelect(tagID, 'TAGS')} 
                        pageID={pageID}
                        pageIDs={data['filters'] && data['filters']['PAGES'] ? data['filters']['PAGES'] 
                                : pageID ? [pageID]
                                : Object.keys(data['pages'])}
                    selected={page['filters'] && page['filters']['TAGS']}
                        styles={{ boxShadow: 'transparent', border: '0px solid transparent', backgroundColor: 'var(--secondary-color)', width: '100%' }}/>
        },
        DATE: {
            title: 'Date',
            icon: <FaCalendarDay/>,
            value: schedule && schedule.valid && 
                    <Tag label={<Schedule dates={{start: schedule.start, end: schedule.end}}/>} 
                        icon={<FaCalendarDay style={{...tagIconStyle}}/>}
                        styles={{ backgroundColor: 'var(--primary-accent-color)', margin: '0 0.5rem 0.2rem 0' }}
                        onDelete={() => {
                            onSelect([null], 'DATE')
                            setTab(null)
                        }}
                        />,
            input: <DateSelector type='dateRange' 
                        startDate={page['filters'] && page['filters']['START_DATE'] && moment(page['filters']['START_DATE'])} 
                        endDate={page['filters'] && page['filters']['END_DATE'] && moment(page['filters']['END_DATE'])}
                        onSelect={value => onSelect(value, 'DATE')}
                        styles={{ backgroundColor: 'var(--secondary-color)', padding: '0', margin: '0', boxShadow: 'transparent', border: '0px solid transparent' }}/>
        },
        PAGES: {
            title: 'Pages',
            icon: <FaFileAlt/>,
            value: page['filters'] && page['filters']['PAGES'] && page['filters']['PAGES'].map(pageID => (
                <Tag label={data['pages'][pageID]['page_name']} 
                    icon={<FaFileAlt style={{...tagIconStyle}}/>}
                    styles={tagStyle}
                    onDelete={() => onSelect(pageID, 'PAGES')}
                    />
            )),
            input: <PageSelector onSelect={pageID => onSelect(pageID, 'PAGES')}
                        selected={page['filters'] && page['filters']['PAGES']}
                        styles={{width: '100%'}} disableSubmitButton/>
        }
    }
    Object.values(options).map((option, index) => {
        if(option['title'] === 'Pages' && pageID) { return }
        blocks.push(
            <Column>
                {/* Tab */}
                <Row onClick={() => setTab(tab !== option['title'] && option['title'])}>
                    <Icon color='var(--secondary-accent-color)'>{option['icon']}</Icon>
                    <Text>{option['title']}</Text>
                    <span/>
                    {/* <FaAngleRight style={{color: 'var(--secondary-accent-color)'}}/> */}
                    <ToggleButton open={tab === option['title']} 
                        styles={{color: 'var(--secondary-accent-color)', padding: 0, margin: 0, fontSize: '0.8rem'}} 
                        icon={<FaAngleUp/>} end={180} alignment='center'/>
                </Row>

                {/* Selected */}
                {/* <SelectedContainer show={option['value'] && option['value'].length > 0} selected={tab === option['title']}>{option['value']}</SelectedContainer> */}

                {/* Selector */}
                <AnimatePresence initial={false}>
                    {tab === option['title'] && <Animation animation='expand' duration={0.3}><div style={{borderBottom: index !== Object.values(options).length - 1 && '1px solid var(--primary-accent-color)', borderTop: index !== Object.values(options).length - 1 && '1px solid var(--primary-accent-color)', padding: '0.25rem 0'}}>{option['input']}</div></Animation>}
                </AnimatePresence>
            </Column>
        )

        if(Array.isArray(option['value'])){
            selected.push([...option['value']])
        } else {
            selected.push(option['value'])
        }
    })
    return (
        <Container>
            <Header>
                <Title>Filter</Title>
                <Button text='Clear' onSelect={onClear}
                    styles={{ margin: 0, color: 'var(--text-color)', fontWeight: 'bold', fontSize: '0.7rem', backgroundColor: 'var(--secondary-color)', boxShadowColor: 'transparent' }}/>
            </Header>
            {selected.length > 0 && <SelectedContainer>{selected}</SelectedContainer>}
            {blocks}
        </Container>
    )
}

export default FilterOptions